import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";

import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import BadgeIcon from "@mui/icons-material/Badge";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import AddJudokaDialog from "../components/AddJudokaDialog";
import ScanLicenseDialog from "../components/ScanLicenseDialog";
import LicenseLookupDialog from "../components/LicenseLookupDialog";
import ExcelImportDialog from "../components/ExcelImportDialog";
import { getAmountDue } from "../utils/categories";
import type { Judoka } from "../models/Judoka";
import type { LicencePrefill } from "../models/LicencePrefill";
import { API_URL } from "../config/api";
import { logActivity } from "../services/activity";

export default function ManageRegistration() {
  const [judokas, setJudokas] = useState<Judoka[]>([]);

  const [addOpen, setAddOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [licenseOpen, setLicenseOpen] = useState(false);
  const [excelOpen, setExcelOpen] = useState(false);
  const [prefill, setPrefill] = useState<LicencePrefill | undefined>();
  const [prefillKey, setPrefillKey] = useState(0);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmSeverity, setConfirmSeverity] = useState<
    "success" | "warning"
  >("success");
  const navigate = useNavigate();

  const registrationCode =
    localStorage.getItem(
      "registrationCode"
    );

  // console.log(
  //   "ManageRegistration - code :",
  //   registrationCode
  // );

  const loadRegistration = async () => {

    const response = await fetch(
      `${API_URL}/registration/${registrationCode}`
    );

    if (!response.ok) {
      // localStorage.removeItem("registrationCode");
      navigate("/registration");
      return;
    }

    const data = await response.json();

    setJudokas(data.judokas ?? []);
  };

  useEffect(() => {

    if (!registrationCode) {
      navigate("/registration");
      return;
    }

    loadRegistration();

    //  }, [navigate, registrationCode]);
  }, []);

  const refreshJudokas = async () => {
    await loadRegistration();
  };

  const handleLicenceScanned = useCallback(
    (scanned: LicencePrefill) => {
      setPrefill(scanned);
      setPrefillKey((k) => k + 1);
      setScanOpen(false);
      setAddOpen(true);
      logActivity("add_opened", { mode: "qr" });
    },
    []
  );

  const handleLicenceFound = useCallback(
    (found: LicencePrefill) => {
      setPrefill(found);
      setPrefillKey((k) => k + 1);
      setLicenseOpen(false);
      setAddOpen(true);
      logActivity("add_opened", { mode: "licence" });
    },
    []
  );

  const handleJudokaAdded = async (emailSent: boolean) => {
    await refreshJudokas();

    setConfirmSeverity(emailSent ? "success" : "warning");
    setConfirmMessage(
      emailSent
        ? "Judoka ajouté. Un email de confirmation a été envoyé."
        : "Judoka ajouté, mais l'email de confirmation n'a pas pu être envoyé (email manquant ou erreur SMTP)."
    );
  };

  const handleJudokasImported = async (
    emailsSent: number,
    total: number
  ) => {
    await refreshJudokas();

    setConfirmSeverity(
      emailsSent === total ? "success" : "warning"
    );
    setConfirmMessage(
      emailsSent === total
        ? `Import terminé (${total} judoka${total > 1 ? "s" : ""}). Un email de confirmation a été envoyé pour chaque adresse.`
        : `Import terminé (${total} judoka${total > 1 ? "s" : ""}), mais l'email n'a pu être envoyé que pour ${emailsSent} adresse(s) sur ${total}.`
    );
  };

  const amountDueOf = (judoka: Judoka) =>
    judoka.amountDue ?? getAmountDue(judoka.category);

  const payingJudokas = judokas.filter(
    (j) => amountDueOf(j) > 0
  );

  const totalDue = judokas.reduce(
    (sum, j) => sum + amountDueOf(j),
    0
  );

  const paymentClubName =
    payingJudokas[0]?.clubName ??
    judokas[0]?.clubName ??
    "";

  const communication =
    payingJudokas.length === 1
      ? `OS 2027 ${paymentClubName} ${payingJudokas[0].firstName} ${payingJudokas[0].lastName} ${payingJudokas[0].category}`
      : `OS 2027 ${paymentClubName} ${payingJudokas.length} judokas`;

  const copyCommunication = () => {
    navigator.clipboard.writeText(communication);
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: "auto",
        px: 3,
        py: 4,
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        fontWeight="bold"
      >
        Mes inscriptions
      </Typography>

      <Stack
        spacing={2}
        useFlexGap
        direction={{ xs: "column", md: "row" }}
        alignItems="stretch"
        sx={{ mb: 4 }}
      >
        <Paper variant="outlined" sx={{ p: 2, flex: 2 }}>
          <Typography fontWeight="bold">
            Judokas de la Fédération Wallonie-Bruxelles
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Le formulaire est pré-rempli et la licence est vérifiée.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap>
            {/* Scan réservé aux petits écrans (téléphone) : sans intérêt sur ordinateur. */}
            <Button
              startIcon={<QrCodeScannerIcon />}
              variant="contained"
              color="error"
              onClick={() => setScanOpen(true)}
              sx={{ display: { xs: "inline-flex", md: "none" } }}
            >
              Scanner une licence
            </Button>

            <Button
              startIcon={<BadgeIcon />}
              variant="contained"
              color="error"
              onClick={() => {
                setLicenseOpen(true);
                logActivity("licence_lookup_opened");
              }}
            >
              Entrer un n° de licence
            </Button>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography fontWeight="bold">
            Judokas étrangers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Uniquement si le judoka n'a pas de licence de la Fédération
            Wallonie-Bruxelles.
          </Typography>

          <Button
            startIcon={<PersonAddIcon />}
            variant="outlined"
            onClick={() => {
              // Ne pas reprendre les données d'une licence lue juste avant.
              if (prefill) {
                setPrefill(undefined);
                setPrefillKey((k) => k + 1);
              }
              setAddOpen(true);
              logActivity("add_opened", { mode: "manual" });
            }}
          >
            Saisie manuelle
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography fontWeight="bold">
            Inscription groupée
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Plusieurs judokas d'un coup, depuis un fichier Excel.
          </Typography>

          <Button
            startIcon={<UploadFileIcon />}
            variant="outlined"
            onClick={() => {
              setExcelOpen(true);
              logActivity("import_opened");
            }}
          >
            Import Excel
          </Button>
        </Paper>
      </Stack>

      <Typography
        variant="h5"
        gutterBottom
      >
        Judokas inscrits ({judokas.length})
      </Typography>

      {judokas.length === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography>
            Aucun judoka inscrit pour le moment.
          </Typography>
        </Paper>
      )}

      {/* 
      {judokas.length > 0 && (
        <Stack spacing={2}>
          {judokas.map((judoka) => (
            <Paper
              key={judoka.id}
              sx={{
                p: 2,
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
              >
                {judoka.lastName} {judoka.firstName}
              </Typography>

              <Typography variant="body2">
                Licence : {judoka.licence}
              </Typography>

              <Typography variant="body2">
                Catégorie : {judoka.category}
              </Typography>

              <Typography variant="body2">
                Club : {judoka.clubName}
              </Typography>

              <Typography variant="body2">
                Nationalité : {judoka.nationality}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )} */}

      {judokas.length > 0 && (
        <Stack spacing={2}>
          {judokas.map((judoka) => (
            <Paper
              key={judoka.id}
              sx={{
                p: 2,
                borderLeft: "5px solid #f44336",
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
              >
                {judoka.firstName} {judoka.lastName}
              </Typography>

              <Typography color="text.secondary">
                {judoka.category} • {judoka.clubName}
              </Typography>

              <Typography variant="body2">
                Licence : {judoka.licence}
              </Typography>

              <Typography variant="body2">
                Montant à payer :{" "}
                {amountDueOf(judoka) > 0
                  ? `${amountDueOf(judoka)} €`
                  : "Gratuit"}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}

      {judokas.length > 0 && (
        <>
          <Divider sx={{ my: 4 }} />

          <Typography variant="h5" gutterBottom>
            Paiement
          </Typography>

          {totalDue === 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography>
                Aucun paiement n'est requis (catégories U9 à U13
                gratuites).
              </Typography>
            </Paper>
          )}

          {totalDue > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography sx={{ mb: 2 }}>
                Gratuit pour les judokas U9 à U13, 10 € pour les
                judokas U15 et U18.
              </Typography>

              <Typography fontWeight="bold" sx={{ mb: 2 }}>
                Montant total à payer : {totalDue} €
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Versement au compte</TableCell>
                      <TableCell>
                        <strong>BE28 1030 2305 2120</strong>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell>BIC</TableCell>
                      <TableCell>
                        <strong>NICABEBB</strong>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell>Communication</TableCell>
                      <TableCell>
                        <strong>{communication}</strong>

                        <IconButton
                          size="small"
                          onClick={copyCommunication}
                          sx={{ ml: 1 }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2 }}
              >
                Inscription individuelle : nom du club + nom du
                judoka + catégorie d'âge.
                <br />
                Paiement groupé : nom du club + nombre de
                judokas.
              </Typography>
            </Paper>
          )}
        </>
      )}

      <AddJudokaDialog
        key={prefillKey}
        initial={prefill}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={handleJudokaAdded}
      />

      <ScanLicenseDialog
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScanned={handleLicenceScanned}
      />

      <LicenseLookupDialog
        open={licenseOpen}
        onClose={() => setLicenseOpen(false)}
        onFound={handleLicenceFound}
      />

      <ExcelImportDialog
        open={excelOpen}
        onClose={() => setExcelOpen(false)}
        onImported={handleJudokasImported}
      />

      <Snackbar
        open={!!confirmMessage}
        autoHideDuration={
          confirmSeverity === "success" ? 5000 : null
        }
        onClose={(_event, reason) => {
          if (reason === "clickaway") {
            return;
          }
          setConfirmMessage("");
        }}
      >
        <Alert
          onClose={() => setConfirmMessage("")}
          severity={confirmSeverity}
          variant="filled"
        >
          {confirmMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}